import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { formatApiError } from "../context/AuthContext";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Loader2, HardHat, FileSignature, Save, MapPin, TrendingUp, TrendingDown, Sparkles, Plus, Trash2, Package, FileText } from "lucide-react";

const eur = (n) => (n == null ? "—" : Number(n).toLocaleString("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }));
const pct = (n) => (n == null ? "—" : `${Number(n).toLocaleString("it-IT", { maximumFractionDigits: 1 })}%`);

export default function PartnerAssignment({ lead }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [installerId, setInstallerId] = useState("");
  const [extra, setExtra] = useState("0");
  const [note, setNote] = useState("");
  const [distinta, setDistinta] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/leads/${lead.id}/partner-assignment`)
      .then((r) => {
        setData(r.data);
        setInstallerId(r.data.installer_id || "");
        setExtra(String(r.data.extra_delta ?? 0));
        setNote(r.data.note || "");
        setDistinta((r.data.distinta || []).map((d) => ({ qta: d.qta ?? 1, codice: d.codice || "", descrizione: d.descrizione || "", valore: d.valore ?? "" })));
      })
      .catch((e) => setErr(e.response?.data?.detail || "Non disponibile"))
      .finally(() => setLoading(false));
  }, [lead.id]);

  useEffect(() => { load(); }, [load]);

  const corrispettivoPreview = data ? Number(data.defaults?.corrispettivo_base || 0) + Number(extra || 0) : 0;
  const distintaTot = distinta.reduce((s, d) => s + (d.valore === "" || d.valore == null ? 0 : Number(d.valore) || 0), 0);

  const updateRow = (i, key, val) => setDistinta((ds) => ds.map((d, idx) => (idx === i ? { ...d, [key]: val } : d)));
  const removeRow = (i) => setDistinta((ds) => ds.filter((_, idx) => idx !== i));
  const addRow = () => setDistinta((ds) => [...ds, { qta: 1, codice: "", descrizione: "", valore: "" }]);

  const save = async () => {
    if (!installerId) { toast.error("Seleziona un installatore partner"); return; }
    setSaving(true);
    try {
      const payload = {
        installer_id: installerId, extra_delta: Number(extra || 0), note,
        distinta: distinta
          .filter((d) => (d.codice || "").trim() || (d.descrizione || "").trim())
          .map((d) => ({ qta: Number(d.qta) || 0, codice: (d.codice || "").trim(), descrizione: (d.descrizione || "").trim(), valore: d.valore === "" || d.valore == null ? null : Number(d.valore) })),
      };
      const r = await api.put(`/leads/${lead.id}/partner-assignment`, payload);
      setData(r.data);
      setExtra(String(r.data.extra_delta ?? 0));
      setDistinta((r.data.distinta || []).map((d) => ({ qta: d.qta ?? 1, codice: d.codice || "", descrizione: d.descrizione || "", valore: d.valore ?? "" })));
      toast.success("Incarico installatore salvato");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const openPdf = () => {
    if (!data?.assigned) { toast.error("Salva prima l'incarico"); return; }
    window.open(`/leads/${lead.id}/subappalto`, "_blank");
  };

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  if (err) return (
    <Card className="p-6 text-center text-muted-foreground" data-testid="partner-assignment-unavailable">
      <HardHat className="h-8 w-8 mx-auto mb-3 text-muted-foreground/60" />
      <p className="text-sm">{err}</p>
      <p className="text-xs mt-1">L'incarico dell'installatore partner è disponibile solo per gli ordini confermati.</p>
    </Card>
  );

  const m = data.margini || {};
  const margineGiu = (m.margine_post_pct ?? 0) < (m.margine_pre_pct ?? 0);
  const suggeritiIds = new Set((data.suggeriti || []).map((s) => s.id));

  return (
    <div className="space-y-6" data-testid="partner-assignment">
      <div className="flex items-center gap-2">
        <HardHat className="h-5 w-5 text-primary" />
        <h3 className="font-heading font-bold text-lg">Incarico Installatore Partner</h3>
      </div>
      <p className="text-sm text-muted-foreground -mt-3">
        Commessa <span className="font-mono text-foreground">{data.commessa || "—"}</span>
        {data.data_contratto ? ` · ${data.data_contratto}` : ""}
      </p>

      {/* Materiali e servizi forniti (editabile, prima del calcolo) */}
      <Card className="p-5 space-y-3" data-testid="partner-assignment-distinta">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 font-semibold text-sm">
            <Package className="h-4 w-4 text-primary" /> Materiali e servizi forniti (Art. 5)
          </div>
          <Button size="sm" variant="outline" onClick={addRow} data-testid="distinta-add">
            <Plus className="h-4 w-4 mr-1.5" /> Aggiungi voce
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Materiali/servizi a nostro carico e forniti all'installatore. Modificabili prima del calcolo; il materiale elettrico è escluso (a carico dell'installatore).
        </p>
        <div className="hidden sm:grid grid-cols-12 gap-2 text-[11px] uppercase tracking-wide text-muted-foreground px-1">
          <span className="col-span-1">Q.tà</span>
          <span className="col-span-3">Codice / Voce</span>
          <span className="col-span-5">Descrizione</span>
          <span className="col-span-2">Valore €</span>
          <span className="col-span-1" />
        </div>
        <div className="space-y-2">
          {distinta.map((d, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`distinta-row-${i}`}>
              <Input className="col-span-3 sm:col-span-1" type="number" step="1" value={d.qta}
                onChange={(e) => updateRow(i, "qta", e.target.value)} data-testid={`distinta-qta-${i}`} />
              <Input className="col-span-9 sm:col-span-3" value={d.codice} placeholder="Codice / Voce"
                onChange={(e) => updateRow(i, "codice", e.target.value)} data-testid={`distinta-codice-${i}`} />
              <Input className="col-span-8 sm:col-span-5" value={d.descrizione} placeholder="Descrizione"
                onChange={(e) => updateRow(i, "descrizione", e.target.value)} data-testid={`distinta-descrizione-${i}`} />
              <Input className="col-span-3 sm:col-span-2" type="number" step="0.01" value={d.valore} placeholder="—"
                onChange={(e) => updateRow(i, "valore", e.target.value)} data-testid={`distinta-valore-${i}`} />
              <Button className="col-span-1" size="icon" variant="ghost" onClick={() => removeRow(i)}
                data-testid={`distinta-remove-${i}`}><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>
            </div>
          ))}
          {distinta.length === 0 && (
            <p className="text-xs text-muted-foreground py-2">Nessuna voce. Usa "Aggiungi voce" per inserirne.</p>
          )}
        </div>
        {distintaTot > 0 && (
          <div className="flex justify-end text-xs text-muted-foreground pt-1 border-t border-border/60">
            Valore materiali forniti: <span className="font-semibold text-foreground ml-1" data-testid="distinta-totale">{eur(distintaTot)}</span>
          </div>
        )}
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Selezione + corrispettivo */}
        <Card className="p-5 space-y-4">
          <div>
            <label className="text-sm font-semibold mb-1.5 block">Installatore partner</label>
            <Select value={installerId} onValueChange={setInstallerId}>
              <SelectTrigger data-testid="partner-assignment-installer-select"><SelectValue placeholder="Seleziona un installatore" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {(data.installers || []).map((i) => (
                  <SelectItem key={i.id} value={i.id} data-testid={`installer-option-${i.id}`}>
                    {suggeritiIds.has(i.id) ? "⭐ " : ""}{i.ragione_sociale}{i.comune ? ` — ${i.comune}${i.provincia ? ` (${i.provincia})` : ""}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {(data.suggeriti || []).length > 0 && (
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-3" data-testid="partner-assignment-suggeriti">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-primary mb-2">
                <Sparkles className="h-3.5 w-3.5" /> Installatori più vicini al cantiere
              </div>
              <div className="space-y-1.5">
                {data.suggeriti.map((s) => (
                  <button key={s.id} onClick={() => setInstallerId(s.id)}
                    className="w-full text-left flex items-center justify-between gap-2 text-xs rounded-md px-2 py-1.5 hover:bg-primary/10 transition-colors"
                    data-testid={`suggerito-${s.id}`}>
                    <span className="flex items-center gap-1.5 truncate"><MapPin className="h-3 w-3 shrink-0 text-muted-foreground" />{s.ragione_sociale}</span>
                    <span className="font-mono text-muted-foreground shrink-0">{s.distanza_km != null ? `${s.distanza_km} km` : ""}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-semibold mb-1.5 block">Corrispettivo base</label>
              <Input value={eur(data.defaults?.corrispettivo_base)} readOnly disabled data-testid="partner-assignment-base" />
              <p className="text-[11px] text-muted-foreground mt-1">{(data.defaults?.voci || []).filter((v) => Number(v.importo) > 0).map((v) => `${v.voce} ${eur(v.importo)}`).join(" + ") || "—"}</p>
            </div>
            <div>
              <label className="text-sm font-semibold mb-1.5 block">Extra (+/−) €</label>
              <Input type="number" step="1" value={extra} onChange={(e) => setExtra(e.target.value)} data-testid="partner-assignment-extra" />
            </div>
          </div>

          <div className="rounded-lg bg-muted/40 p-3 flex items-center justify-between">
            <span className="text-sm font-semibold">Corrispettivo subappalto (a corpo)</span>
            <span className="font-heading font-bold text-lg" data-testid="partner-assignment-corrispettivo">{eur(corrispettivoPreview)}</span>
          </div>

          <div>
            <label className="text-sm font-semibold mb-1.5 block">Note interne (opzionale)</label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Es. condizioni particolari…" data-testid="partner-assignment-note" />
          </div>

          <div className="flex gap-2">
            <Button onClick={save} disabled={saving} className="flex-1" data-testid="partner-assignment-save">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Salva incarico
            </Button>
            <Button onClick={openPdf} variant="outline" disabled={!data.assigned} data-testid="partner-assignment-pdf">
              <FileSignature className="h-4 w-4 mr-2" />PDF subappalto
            </Button>
          </div>
        </Card>

        {/* Margine + distinta */}
        <div className="space-y-6">
          <Card className="p-5 space-y-3" data-testid="partner-assignment-margini">
            <div className="flex items-center gap-2 font-semibold text-sm">
              {margineGiu ? <TrendingDown className="h-4 w-4 text-amber-500" /> : <TrendingUp className="h-4 w-4 text-emerald-500" />}
              Marginalità effettiva
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-lg border border-border p-3">
                <div className="text-xs text-muted-foreground">Preventivo</div>
                <div className="font-heading font-bold text-lg">{pct(m.margine_pre_pct)}</div>
                <div className="text-xs text-muted-foreground">{eur(m.margine_pre_eur)}</div>
              </div>
              <div className="rounded-lg border border-primary/40 bg-primary/5 p-3">
                <div className="text-xs text-muted-foreground">Con subappalto</div>
                <div className="font-heading font-bold text-lg">{pct(m.margine_post_pct)}</div>
                <div className="text-xs text-muted-foreground">{eur(m.margine_post_eur)}</div>
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Costo commerciale: {eur(m.costo_industriale_pre)} → {eur(m.costo_industriale_post)} · prezzo di vendita {eur(m.prezzo)} (invariato: il cliente ha già firmato).
            </p>
          </Card>

          {data.cost_sheet && (
            <Card className="p-5 space-y-3" data-testid="partner-assignment-cost-sheet">
              <div className="flex items-center gap-2 font-semibold text-sm">
                <FileText className="h-4 w-4 text-primary" /> Scheda costi completa
                <span className="text-[11px] font-normal text-muted-foreground ml-auto">sola lettura</span>
              </div>
              <div className="space-y-1">
                {(data.cost_sheet.items || []).map((it, i) => (
                  <div key={i} className={`flex items-start justify-between gap-3 text-xs py-1 border-b border-border/50 ${it.evidenzia ? "text-amber-600 dark:text-amber-400" : ""}`}>
                    <div className="min-w-0">
                      <div className="font-medium truncate">{it.voce}</div>
                      {it.dettaglio && <div className="text-[10px] text-muted-foreground truncate">{it.dettaglio}</div>}
                    </div>
                    <div className="font-mono shrink-0">{eur(it.importo)}</div>
                  </div>
                ))}
              </div>
              <div className="space-y-1 pt-1 text-xs">
                <div className="flex justify-between"><span className="text-muted-foreground">Costo commerciale</span><span className="font-mono font-semibold">{eur(data.cost_sheet.costo_industriale)}</span></div>
                {data.cost_sheet.margine != null && (
                  <div className="flex justify-between"><span className="text-muted-foreground">Margine</span><span className="font-mono">{pct(Number(data.cost_sheet.margine) * 100)}</span></div>
                )}
                <div className="flex justify-between"><span className="text-muted-foreground">Prezzo (imponibile)</span><span className="font-mono font-semibold">{eur(data.cost_sheet.prezzo)}</span></div>
                {data.cost_sheet.iva_importo != null && (
                  <div className="flex justify-between"><span className="text-muted-foreground">IVA</span><span className="font-mono">{eur(data.cost_sheet.iva_importo)}</span></div>
                )}
                <div className="flex justify-between text-sm pt-1 border-t border-border"><span className="font-semibold">Totale IVA inclusa</span><span className="font-heading font-bold">{eur(data.cost_sheet.prezzo_iva_incluso)}</span></div>
              </div>
              <p className="text-[10px] text-muted-foreground">
                {data.cost_sheet.pv_kwp ? `Impianto ${data.cost_sheet.pv_kwp} kWp` : ""}{data.cost_sheet.storage_kwh ? ` · accumulo ${data.cost_sheet.storage_kwh} kWh` : ""} · valori dal preventivo (non modificabili qui).
              </p>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
